import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Text, Button, Divider, Image
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import FormValidator from 'utils/FormValidator';
import UrlUtils from 'utils/Url';
import userUtils from 'utils/User';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import ErrorList from 'components/ErrorList';
import Markdown from 'components/Markdown/Markdown';
import Empty from 'constants/empty';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';

import { space } from 'style/config';
import RegisterEN from 'components/Register/locales/Register_en_US';
import RegisterFR from 'components/Register/locales/Register_fr_CA';
const getText = localeUtils.getLocalisedDataFromResource(RegisterEN, RegisterFR);

const RegisterEmailLookup = ({
    handleSignIn, handleEmailChange, handleStoreMember, handleLookup, isOAuth2Flow
}) => {
    const dispatch = useDispatch();
    const emailInput = useRef(null);
    const [errorMessages, setErrorMessages] = useState([]);

    // AC 1: Fire s.t()/TrackState on page load for ChatGPT flow
    useEffect(() => {
        if (isOAuth2Flow) {
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageName: anaConsts.CHATGPT_REGISTER_CREATE_ACCOUNT_PAGE_NAME,
                    pageType: anaConsts.PAGE_TYPES.REGISTER,
                    eventStrings: [anaConsts.Event.REGISTRATION_STEP_1, anaConsts.Event.EVENT_313]
                }
            });
        }
    }, [isOAuth2Flow]);

    const isValidLookup = () => {
        const fieldsForValidation = [emailInput.current];
        const errors = FormValidator.getErrors(fieldsForValidation);

        return !errors.fields.length;
    };

    const handleEmailLookup = async () => {
        const email = emailInput.current?.getValue();
        const successCallback = data => {
            if (data.isStoreBiMember) {
                handleStoreMember(true);
            } else {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set('existingUser', email || '');
                UrlUtils.redirectTo(`/login?${urlParams.toString()}`);
            }
        };

        const failureCallback = error => {
            if (userUtils.isEmailDisposableError(error)) {
                emailInput.current?.showError(getText('invalidEmailError'));
            } else if (userUtils.isEmailTypoError(error)) {
                dispatch(
                    Actions.showEmailTypoModal({
                        isOpen: true,
                        isNewUserFlow: true,
                        email,
                        onContinue: () => handleStoreMember(true),
                        onCancel: Empty.Function
                    })
                );
            } else if (userUtils.isEmailVerifyError(error)) {
                handleStoreMember(true);
            } else {
                handleLookup(false);
            }
        };

        await dispatch(UserActions.checkUser(email, successCallback, failureCallback));
    };

    // AC 7: Store prop55 for sign-in page load tracking, fires as s.t()/TrackState on /login
    const handleSignInClick = () => {
        if (isOAuth2Flow) {
            anaUtils.setNextPageData({
                linkData: anaConsts.LinkData.CHATGPT_CREATE_ACCOUNT_SIGN_IN
            });
        }

        handleSignIn();
    };

    const handleLookupSubmit = async e => {
        e.preventDefault();
        setErrorMessages([]);

        if (!isValidLookup()) {
            // AC 2: Fire s.tl()/TrackAction for email validation error in ChatGPT flow
            if (isOAuth2Flow) {
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        bindingMethods: linkTrackingError,
                        eventStrings: [anaConsts.Event.REGISTRATION_ATTEMPT, anaConsts.Event.EVENT_313],
                        pageName: anaConsts.CHATGPT_REGISTER_CREATE_ACCOUNT_PAGE_NAME,
                        pageType: anaConsts.PAGE_TYPES.REGISTER,
                        fieldErrors: `${anaConsts.CHATGPT} registration:emailAddress`,
                        errorMessages: getText('emailFormatError')
                    }
                });
            }

            return;
        }

        try {
            await handleEmailLookup();
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || getText('genericErrorMessage');

            setErrorMessages([errorMessage]);
        }
    };

    return (
        <Box
            lineHeight='tight'
            is='form'
            onSubmit={handleLookupSubmit}
            noValidate
        >
            <Text
                is='h2'
                fontWeight='bold'
                fontSize='xl'
                marginTop={2}
                children={getText('title')}
            />
            <Image
                alt='Beauty Insider'
                disableLazyLoad={true}
                display='block'
                src='/img/ufe/bi/logo-beauty-insider.svg'
                width={194}
                height={32}
                marginBottom={4}
                marginTop={5}
            />

            <Markdown
                marginBottom={4}
                content={getText('joinBiFreeShip')}
            />

            {errorMessages.length > 0 && (
                <Box css={styles.errorContainer}>
                    <ErrorList errorMessages={errorMessages} />
                </Box>
            )}

            <InputEmail
                marginBottom={4}
                required={true}
                name='email'
                label={getText('emailAddresLabel')}
                hideAsterisk={true}
                ref={emailInput}
                onChange={handleEmailChange}
            />
            <Button
                variant='primary'
                width='100%'
                type='submit'
            >
                {getText('confirmButton')}
            </Button>
            <Divider marginY={4} />
            <b>{getText('alreadyHaveAccount')}</b>
            <Button
                width='172'
                display='block'
                variant='secondary'
                marginTop={3}
                paddingX={7}
                onClick={handleSignInClick}
            >
                {getText('signIn')}
            </Button>
        </Box>
    );
};

const styles = {
    errorContainer: {
        marginBottom: space[4]
    }
};

export default wrapFunctionalComponent(RegisterEmailLookup, 'RegisterEmailLookup');
